<template>
  <div class="c-btns-container" v-if="!isSessionPendingRef">
    <CustomButton
      usage="button"
      data-test-id="session-decline-button"
      type="submit"
      color="danger"
      :text="t('ui.button.decline')"
      @emit:click="cancelSession"
    />
    <CustomButton
      v-if="canEditSessionRef"
      usage="button"
      type="submit"
      color="primary"
      text="Modifier"
      @emit:click="openEditSessionModal"
    >
      <template #icon>
        <EditIcon color="white" />
      </template>
    </CustomButton>
    <CustomButton
      usage="button"
      data-test-id="session-accept-button"
      type="submit"
      color="primary"
      :text="t('ui.button.accept')"
      @emit:click="acceptSession"
    />
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed } from 'vue'
import { toast } from 'vue-sonner'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { V1UpdateSessionStatusMutation } from '@/features/sessions/api'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import EditIcon from '@/features/ui/components/icons/edit-icon.vue'
import { selectGetSessionDetailsModal } from '@/features/ui/store/modal/modal.selectors'
import type { EditSessionModalType } from '@/features/ui/store/modal/modal.store'
import type { ConfirmActionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import { useI18n } from '@/lib/i18n'
import { invalidateSession, invalidateSessions } from '@/utils/lib/vue-query'

const { t } = useI18n()

const { accountRef } = useAccount()

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(selectedProgramIdRef)

const sessionDetailsModalRef = selectGetSessionDetailsModal(modalsStore)

const sessionIdRef = computed(() => sessionDetailsModalRef.value.attachedData.sessionId ?? null)
const { sessionRef, isPendingRef: isSessionPendingRef } = useSession(sessionIdRef)
const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId)
const { groupRolesRef } = useGroupRoles({ groupIdRef })

const canEditSessionRef = computed(
  () =>
    groupRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) ||
    (groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE) && programRef.value?.settings.menteeCanBookSession),
)

function openEditSessionModal() {
  modalsStore.toggleModal('session-details', false)
  modalsStore.toggleModal('edit-session', true)
  modalsStore.updateModalAttachedData<EditSessionModalType>('edit-session', {
    sessionId: sessionIdRef.value!,
  })
}

function acceptSession() {
  if (sessionRef.value?.id && accountRef.value?.id) {
    updateSessionStatus({
      sessionId: sessionRef.value.id,
      status: SessionStatusEnumType.PLANNED,
    })
  } else {
    toast?.error(t('error.common'))
    throw new Error('Session or account not found')
  }
}

// update the session status
const { mutate: updateSessionStatus } = useMutation({
  mutationFn: V1UpdateSessionStatusMutation,
  onSuccess(data, variables) {
    modalsStore.toggleModal('session-details', false)
    invalidateSessions()
    invalidateSession(sessionIdRef.value!)
    // show a success message based on the status
    if (variables.status === SessionStatusEnumType.CANCELLED) {
      toast?.success(t('session.cancel.success'))
    } else {
      toast?.success(t('session.accept.success'))
    }
  },
  onError() {
    modalsStore.toggleModal('session-details', false)
    toast?.error(t('error.common'))
  },
})

function cancelSession() {
  modalsStore.toggleModal('confirm-action', true)
  modalsStore.updateModalAttachedData<ConfirmActionModalType>('confirm-action', {
    title: t('session.cancel.confirmation.title'),
    content: t('ui.modal.confirmation.irreversible'),
    cancelFn() {
      modalsStore.toggleModal('confirm-action', false)
    },
    fn() {
      if (sessionRef.value?.id && accountRef.value?.id) {
        updateSessionStatus({
          sessionId: sessionRef.value.id,
          status: SessionStatusEnumType.CANCELLED,
        })
      } else {
        toast?.error(t('error.common'))
        throw new Error('Session or account not found')
      }
    },
  })
}
</script>
